import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Monitor, MonitorPlay, Megaphone, Copy, Check, ArrowRight } from "lucide-react";
import { roomsApi, roomQueryKeys, type Room } from "../../shared/api/rooms";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { copyText } from "../../shared/clipboard";

/**
 * "Màn hình & thiết bị" (#18583) — one operator-facing place to set up every queue TV, instead of
 * the previous split across /settings/kiosks, /settings/queue-calling and (buried in the catalogue)
 * /settings/rooms. This first version consolidates the ENTRY POINT: the per-room TV links are now
 * here (moved out of the catalogue page), and the shared-token + call-behaviour cards link to their
 * existing pages. Folding those two fully inline is tracked as a follow-up subtask.
 */
export function DisplaysSettingsPage() {
  const [copied, setCopied] = useState<string | null>(null);
  const rooms = useQuery({
    queryKey: roomQueryKeys.list(true),
    queryFn: () => roomsApi.list(true),
  });

  const displayUrl = (room: Room) =>
    `${window.location.origin}/display/queue?branchId=${room.branchId}&room=${room.id}`;

  const copy = async (r: Room) => {
    if (!(await copyText(displayUrl(r)))) return;
    setCopied(r.id);
    setTimeout(() => setCopied(null), 1500);
  };

  const list = rooms.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Màn hình & thiết bị"
        description="Dựng và quản lý mọi màn hình TV hàng đợi ở một nơi — TV phòng chờ chung, TV theo phòng, và hành vi gọi số."
      />

      {/* 1 · Shared waiting-room TV */}
      <Card>
        <CardHeader className="flex-row items-center gap-2 space-y-0">
          <Monitor className="size-5 text-muted-foreground" />
          <CardTitle className="text-base">TV phòng chờ chung</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <p className="mb-0 text-sm text-muted-foreground">
            Màn hình lớn ở sảnh hiển thị toàn bộ hàng chờ. Cấp / thu hồi token và mở link trên TV.
          </p>
          <Button asChild variant="secondary">
            <Link to="/settings/kiosks">
              Mở cấu hình token <ArrowRight className="size-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* 2 · Per-room TV links (moved here from the catalogue page) */}
      <Card>
        <CardHeader className="flex-row items-center gap-2 space-y-0">
          <MonitorPlay className="size-5 text-muted-foreground" />
          <CardTitle className="text-base">TV theo từng phòng</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 mt-0 text-sm text-muted-foreground">
            Mỗi phòng một màn hình nhỏ hiển thị số đang gọi vào phòng đó.
          </p>
          {rooms.isLoading ? (
            <p className="text-sm text-muted-foreground">Đang tải…</p>
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Chưa có phòng nào. Thêm phòng ở{" "}
              <Link className="underline" to="/settings/rooms">
                Danh mục → Buồng khám
              </Link>
              .
            </p>
          ) : (
            <div className="divide-y rounded-md border">
              {list.map((r) => (
                <div
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium text-foreground">
                      {r.code ? `${r.code} · ` : ""}
                      {r.name}
                    </div>
                    <div className="truncate font-mono text-xs text-muted-foreground">
                      {displayUrl(r)}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      title="Mở màn hình hàng đợi của phòng này trên TV/màn hình tại phòng"
                      onClick={() => window.open(displayUrl(r), "_blank")}
                    >
                      <MonitorPlay /> Mở màn hình
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => copy(r)}
                    >
                      {copied === r.id ? <Check /> : <Copy />}
                      {copied === r.id ? "Đã chép" : "Sao chép"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3 · Call behaviour */}
      <Card>
        <CardHeader className="flex-row items-center gap-2 space-y-0">
          <Megaphone className="size-5 text-muted-foreground" />
          <CardTitle className="text-base">Hành vi gọi số</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <p className="mb-0 text-sm text-muted-foreground">
            Cách hệ thống tự động gọi bệnh nhân tiếp theo vào phòng (auto-call-next, cỡ nhóm chuẩn bị).
          </p>
          <Button asChild variant="secondary">
            <Link to="/settings/queue-calling">
              Mở cấu hình gọi số <ArrowRight className="size-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
